import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { MyCalendar } from "../../components/employee/MyCalendar";
import { useAuth } from "../../context/AuthContext";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function MyAttendance() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [view, setView] = useState("calendar");
  const [list, setList] = useState([]);
  const [regs, setRegs] = useState([]);
  const [showReg, setShowReg] = useState(false);
  const [form, setForm] = useState({ date: "", requested_in: "09:00", requested_out: "18:00", reason: "" });

  useEffect(() => { api.get("/attendance/me", { params: { month } }).then((r) => setList(r.data)); }, [month]);
  const loadRegs = useCallback(() => api.get("/regularizations/me").then((r) => setRegs(r.data)), []);
  useEffect(() => { loadRegs(); }, [loadRegs]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/regularizations", form);
      toast.success("Regularization request submitted");
      setShowReg(false); setForm({ date: "", requested_in: "09:00", requested_out: "18:00", reason: "" });
      loadRegs();
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="space-y-5" data-testid="my-attendance-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold">My attendance</h1>
        <button onClick={() => setShowReg(!showReg)} data-testid="toggle-regularization-form"
          className="text-sm font-bold text-primary">{showReg ? "Close" : "Missed punch?"}</button>
      </div>

      {showReg && (
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
          <h3 className="font-heading font-bold text-sm">Request regularization</h3>
          <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reg-date-input" className="w-full min-h-[48px] px-3 rounded-lg border border-stone-300" />
          <div className="grid grid-cols-2 gap-3">
            <input type="time" required value={form.requested_in} onChange={(e) => setForm({ ...form, requested_in: e.target.value })} data-testid="reg-in-input" className="min-h-[48px] px-3 rounded-lg border border-stone-300" />
            <input type="time" required value={form.requested_out} onChange={(e) => setForm({ ...form, requested_out: e.target.value })} data-testid="reg-out-input" className="min-h-[48px] px-3 rounded-lg border border-stone-300" />
          </div>
          <input placeholder="Reason" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="reg-reason-input" className="w-full min-h-[48px] px-3 rounded-lg border border-stone-300" />
          <button type="submit" data-testid="submit-regularization-button" className="tap-scale w-full min-h-[48px] rounded-full bg-primary text-white font-bold hover:bg-[#A63F07] transition-colors duration-200">Submit request</button>
        </form>
      )}

      {regs.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm mb-2">My regularization requests</h3>
          {regs.map((r) => (
            <div key={r.id} className="flex justify-between text-sm py-2 border-b border-stone-100 last:border-0">
              <span>{r.date} · {r.requested_in}–{r.requested_out}</span>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="attendance-month-input"
          className="min-h-[48px] px-4 rounded-lg border border-stone-300 bg-white flex-1" />
        <div className="flex rounded-full border border-stone-200 bg-white p-1">
          {[["calendar", "Calendar"], ["list", "List"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`attendance-view-${k}`}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors duration-200 ${view === k ? "bg-secondary text-white" : "text-stone-500"}`}>{label}</button>
          ))}
        </div>
      </div>

      {view === "calendar" && <MyCalendar userId={user?.id} month={month} />}

      {view === "list" && (
      <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
        {list.length === 0 && <p className="p-8 text-center text-sm text-stone-400">No records for this month.</p>}
        {list.map((r) => (
          <div key={r.id} className="p-4 flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm tabular-nums">{r.date}</div>
              <div className="text-xs text-stone-500 tabular-nums">{r.check_in || "—"} → {r.check_out || "—"}{r.regularized ? " · regularized" : ""}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-sm tabular-nums">{r.hours ?? "—"} h</div>
              {r.overtime_hours > 0 && <div className="text-xs text-primary font-semibold">+{r.overtime_hours}h OT</div>}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}